/* eslint-disable indent */
import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useTheme } from '@mui/material';
import { borderWidthThin, fontWeightBold, spacing60, spacing80, spacing50, spacing40, spacing30, spacing20, colorCtaBlueBase, colorTextNeutral600, colorBrandNeutral100, fontSizeDefault } from '@ellucian/react-design-system/core/styles/tokens';
import { INLINE_VARIANT, makeStyles, useMediaQuery, Alert, IconButton, Paper, Typography, Tooltip, Button, SimpleDialog } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import { useHistory, matchPath, generatePath, useParams } from 'react-router-dom';
import findIndex from 'lodash/findIndex';
import useApplicationForm from './useApplicationForm';
import { routes, applicantRoute, CONSTANTS } from '../Helpers/Configuration';
import { useSnackbar } from '../SnackbarContext/SnackbarContext';
import moment from 'moment';
import { useDispatch, useSelector, batch } from 'react-redux';
import { checkForceShowErrors } from '@nsr/react-form';
import { goBackAction, goForwardAction, setPageAndCloseDialogAction, setPagesInitialStatusAction } from './redux/actions/navigationActions';
import { setSettingsValue } from './redux/slices/settingsSlice';
import isInIframe from '../../utilities/isInIframe';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice.js';
import ExperienceContext from '../../ExperienceContext';
import { setPageHeader } from '../../redux/admin/slices/adminBaseSlice';
import { setApplicationPageCompletedStatus, setApplication, setOriginalApplicationValues } from './redux/slices/applicationSlice';
import { findChangedValues, getEmailPersonalizationQuestions, getApplicationValue, isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import forEach from 'lodash/forEach';
import useRoot from '../RootContext/useRoot';
import {
    canEditApplication,
    canViewStagingRecord,
    canViewProcessingHistory,
    canSendEmailApplication
} from '../../utilities/actionCheck';
import { Collapse } from '@mui/material';
import { GlobalStyles } from 'tss-react';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';
import ApplicationFormSideBar from './ApplicationFormSideBar';
import ApplicationFormNavigationBar from './ApplicationFormNavigationBar';
import { Route, Redirect, Switch } from 'react-router-dom';
import SubmittedDialog from './SubmittedDialog';
import ApplicationFormPage from './ApplicationFormPage';
import SupplementalItemsChecklist from './SupplementalItemsChecklist';
import SubmittedDocuments from './SubmittedDocuments';
import ApplicationPayments from './ApplicationPayments';
import InquiryNavigation from './Inquiry/InquiryNavigation/InquiryNavigation';
import InquiryConfirm from '../Inquiry/InquiryConfirm';
import WatchErrors from './redux/WatchErrors';
import AutosaveStatus from './AutosaveStatus';
import Review from './Review';
import ApsSaveWarningDialog from './ApsSaveWarningDialog';
import ApsSaveWarningTable from './ApsSaveWarningTable';
import ExecutionHistoryButton from '../Admin/TransferJobs/ExecutionHistoryButton';
import SendEmailDialog from '../Admin/Email/SendEmailDialog';
import BackArrow from '../Admin/AdminControls/BackArrow';
const { isApplicationFormTemplate } = require('@nsr/applicant-apply-shared/src/helpers/applicationTemplateHelper');

const useStyles = makeStyles({
    name: 'ApplicationForm'
})(theme => ({
    root: {
        margin: `3rem ${spacing80}`,
        paddingTop: spacing30,
        '&.inquiryRoot': {
            margin: '3rem auto 3rem auto',
            maxWidth: '800px',
        },
        '&.mobile': {
            marginTop: spacing30,
            paddingTop: 0,
        },
        '&.embedded': {
            marginTop: 0,
            paddingTop: 0,
        },
        [theme.breakpoints.down('lg')]: {
            margin: `4rem ${spacing40} ${spacing60} ${spacing40}`,
        },
    },
    container: {
        marginTop: spacing30,
        display: 'grid',
        width: '100%',
        [theme.breakpoints.up('md')]: {
            gridTemplateAreas: `
                'sidebar content'
            `,
            gridTemplateColumns: 'max-content 3fr',
        },
        [theme.breakpoints.down('lg')]: {
            gridTemplateAreas: `
                'content'
            `,
            gridTemplateColumns: '1fr',
        },
    },
    iconRoot: {
        marginRight: spacing30,
    },
    content: {
        display: 'grid',
        gridArea: 'content',
        gridTemplateRows: 'max-content',
        padding: spacing50,
        minWidth: 0,
        overflow: 'hidden',
    },
    togglePanelButton: {
        margin: spacing40,
    },
    sidebar: {
        gridArea: 'sidebar',
        borderRight: `${borderWidthThin} solid rgba(0, 0, 0, 0.12)`,
        minHeight: '50vh',
        [theme.breakpoints.down('lg')]: {
            display: 'none',
            minHeight: 'unset',
        },
    },
    bottomButtonContainer: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    bottomButton: {
        paddingLeft: spacing50,
        [theme.breakpoints.down('lg')]: {
            paddingLeft: 'spacing40',
        },
    },
    icon: {
        marginRight: spacing30
    },
    formTitle: {
        display: 'block',
        [theme.breakpoints.down('lg')]: {
            fontWeight: fontWeightBold,
        },
    },
    formTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: spacing50,
        width:'100%',
        [theme.breakpoints.down('lg')]: {
            display: 'block',
        },
    },
    returnButtonContainer: {
        marginRight: spacing40,
    },
    titleContainer: {
        [theme.breakpoints.down('lg')]: {
            display: 'flex',
            '& > button': {
                marginRight: spacing40,
                marginTop: spacing30,
            },
        },
    },
    mobilePanel: {
        width: '100%',
        border: `${borderWidthThin} solid rgba(0, 0, 0, 0.12)`,
        borderRadius: '4px',
        marginBottom: spacing40,
        backgroundColor: colorBrandNeutral100,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
    },
    mobileHomeRow: {
        display: 'flex',
        alignItems: 'center',
        padding: `${spacing40} ${spacing40}`,
        cursor: 'pointer',
        '& svg': {
            color: colorCtaBlueBase,
            marginRight: spacing30,
            fontSize: '1.25rem',
        },
    },
    mobileHomeText: {
        color: colorCtaBlueBase,
        fontWeight: fontWeightBold,
        fontSize: fontSizeDefault,
    },
    mobilePanelCollapsed: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: `${spacing30} ${spacing40}`,
        cursor: 'pointer',
        borderTop: `${borderWidthThin} solid rgba(0, 0, 0, 0.12)`,
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    },
    mobilePanelTitle: {
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            color: colorTextNeutral600,
            marginRight: spacing30,
            fontSize: '1.15rem',
        },
    },
    mobilePanelPageText: {
        fontSize: fontSizeDefault,
        color: colorTextNeutral600,
    },
    mobileChevron: {
        color: colorTextNeutral600,
        fontSize: '1.15rem',
        transition: 'transform 0.3s ease',
    },
    mobileChevronOpen: {
        transform: 'rotate(180deg)',
    },
    homeIcon: {
        marginRight: spacing30,
    },
    topBar: {
        display: 'flex'
    },
    readonlyText: {
        padding: spacing20,
    },
    saveBottonHeader: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: spacing30,
        justifyContent: 'flex-end',
        marginLeft: 'auto'
    },
    locale: {
        [theme.breakpoints.down('lg')]: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: spacing30,
        },
    },
}));

const showUseStyles = makeStyles({
    name: 'ApplicationForm'
})(() => ({
    hide: {
        display: 'none',
    }
}));
const Show = ({ children, hide = false }) => {
    const { classes } = showUseStyles();
    return <div className={hide ? classes.hide : ''}>{children}</div>;
};

const selectApplication = state => state.application;
const selectOriginalApplicationValues = state => state.application.originalApplicationValues;
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectApplicationIsArchived = state => state.application?.archivedOn;
const selectApplicationIsWithdrawn = state => state.application?.status === CONSTANTS.applicationFormStatuses.WITHDRAWN;
const selectAllValues = state => state.form.values;
const selectFormErrors = state => state.form.errors;
const selectHiddenPageIds = state => state.settings.hiddenPageIds;
const selectNotifyTemplateUpdate = state => state.settings.notifyTemplateUpdate;
const selectApplicationIsPaymentPending = state => state.application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending;

const ApplicationForm = () => {
    const intl = useIntl();
    const snackbar = useSnackbar();
    const params = useParams();
    const { setExitPrompt } = useContext(ExperienceContext);
    const { localeDirection } = useContext(GlobalLocaleContext);
    const {
        clearApplicationSession,
        externalMode,
        getApplicationFee,
        globalSettings,
        importAdminMode,
        inquiryMode,
        onLeavePreview,
        onViewStager,
        save,
        previewMode,
        supplementalFormMode,
        template = { pages: [] },
        hasPermission,
    } = useApplicationForm();
    const rootCtx = useRoot();
    const isAps = template?.settings?.applicationTemplateType === CONSTANTS.applicationTemplateTypes.import ? true : false;
    const dispatch = useDispatch();
    const history = useHistory();
    const { state: routeState } = history.location;
    const showDialog = useMemo(() => !!routeState?.showDialog, [routeState]);
    const routeMatch = matchPath(history.location.pathname, {
        path: [
            routes.adminTemplatePreviewPageById,
            routes.importsTemplatePreviewPageById,
            routes.applicationFormPageById,
            routes.externalApplicationPageById,
            routes.applicationFormSupplementalForm,
            routes.importsApplicationEditPageById
        ]
    });
    const isOnPayment = useMemo(() => {
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationFormPayments,
                routes.externalApplicationPayments,
            ]
        });
    }, [history.location]);

    const isOnReview = useMemo(() => {
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationFormReviewPage,
                routes.externalApplicationwReviewPage,
                routes.adminTemplatePreviewReviewPage,
                routes.importsTemplatePreviewReviewPage,
            ]
        });
    }, [history.location]);

    const application = useSelector(selectApplication);
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const isArchived = useSelector(selectApplicationIsArchived);
    const isWithdrawn = useSelector(selectApplicationIsWithdrawn);
    const hiddenPageIds = useSelector(selectHiddenPageIds);
    const notifyTemplateUpdate = useSelector(selectNotifyTemplateUpdate);
    const values = useSelector(selectAllValues);
    const originalValues = useSelector(selectOriginalApplicationValues);
    const formErrors = useSelector(selectFormErrors); 
    const isPaymentPending = useSelector(selectApplicationIsPaymentPending);
    const applicationStatus = application?.status;
    const [applicationFee, setApplicationFee] = useState();
    const [showSubmitted, setShowSubmitted] = useState(false);
    const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
    const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
    const [isSaveAdminApplicationOpen, setSaveAdminApplicationOpen] = useState(false);
    const [isEmailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailQuestions, setEmailQuestions] = useState('');
    const [changes, setChanges] = useState(null);
    const currentPageId = routeMatch?.params.pageId;
    const canViewTransferJobs = useMemo(() =>  !!hasPermission && canViewProcessingHistory(hasPermission), [hasPermission]);
    const canViewStagers = useMemo(() => !!hasPermission && canViewStagingRecord(hasPermission), [hasPermission]);
    const canSendEmail = useMemo(() => !!hasPermission && canSendEmailApplication(hasPermission), [hasPermission]);
    
    useEffect(() => {
        const baseTitle = template?.templatePageTitle || globalSettings?.applyPageTitle || '';

        const getSuffix = () => {
            // If this template is an inquiry form, use Inquiry suffix from template messages if present
            if (isAps) {
                // imports use 'Application' wording also
                return ` - ${intl.formatMessage({ id: 'applicationLabel' })}`;
            }
            const isInquiry = template?.templateType && template.templateType.toLowerCase().includes('inquiry');
            if (isInquiry) {
                return ` - ${intl.formatMessage({ id: 'inquiryName' })}`;
            }
            return ` - ${intl.formatMessage({ id: 'applicationLabel' })}`;
        };

        document.title = `${baseTitle}${getSuffix()}`;
    }, [template?.templatePageTitle, template?.templateType, globalSettings, intl, isAps]);

    useEffect(() => {
        if (!isEqual(values, originalValues)) {
            setExitPrompt(true);
        }
        else setExitPrompt(false);
    }, [values, originalValues, setExitPrompt]);

    // Remove exit prompt on unmount
    useEffect(() => { return () => { setExitPrompt(false); }; }, []);

    useEffect(() => {
        setExitPrompt(false);
    }, [history.location, setExitPrompt]);

    //Used to redirect from edit app page to read-only app if user cannot edit apps or app has certain statuses
    const canEditApp = useMemo(() => {
        return !!hasPermission && canEditApplication(hasPermission) &&
            ![CONSTANTS.applicationProcessingStatus.pending,
                CONSTANTS.applicationProcessingStatus.personCheck,
                CONSTANTS.applicationProcessingStatus.fraud].includes(applicationStatus);
    }, [hasPermission, applicationStatus]);

    useEffect(() => {
        if (importAdminMode) {
            dispatch(setPageHeader(intl.formatMessage({ id: 'adminApplicationHeader' })));
        }
    }, []);
    useEffect(() => {
        if ((isSubmitted || isArchived) && routeMatch && !supplementalFormMode) {
            let route = generatePath(routes.applicationFormSubmitPage, { id: application.id });
            if (previewMode && !isAps) {
                route = generatePath(routes.adminTemplatePreviewSubmitPage, { id: application.id });
            }
            if (previewMode && isAps) {
                route = generatePath(routes.importsTemplatePreviewSubmitPage, { id: application.id });
            }
            else if (externalMode) {
                route = generatePath(routes.externalApplicationSubmitPage, { id: application.id });
            }
            history.push(route);
        }
    }, [applicationStatus, externalMode, supplementalFormMode, isSubmitted, isArchived, routeMatch, previewMode, isAps]);

    useEffect(() => {
        if (isWithdrawn && routeMatch && !inquiryMode && !supplementalFormMode) {
            let route = routes.applicationFormWithdrawnPage.replace(':id', application.id);
            history.push(route);
        }
    }, [applicationStatus, inquiryMode, supplementalFormMode, isWithdrawn, routeMatch]);

    useEffect(() => {
        if (!supplementalFormMode && !isArchived) {
            if (template.allApplicationsRemovalAt && !isSubmitted) {
                const archivedDate = moment(template.allApplicationsRemovalAt).add(1, 'days').startOf('day');
                snackbar.show(<FormattedMessage id="applicationFormArchiveWarning" values={{ date: new Date(archivedDate).toLocaleString() }} />);
            }
            else if (applicationStatus === CONSTANTS.applicationFormStatuses.READONLY) {
                snackbar.show(<FormattedMessage id="applicationFormsHomeInactive" />);
            }
            else if (applicationStatus === CONSTANTS.applicationFormStatuses.WITHDRAWN) {
                snackbar.show(<FormattedMessage id="applicationFormsHomeWithdrawn" />);
            }
        }
    }, []);

    useEffect(() => {
        setShowSubmitted(isSubmitted && showDialog && !inquiryMode && !supplementalFormMode);
        setShowPaymentProcessing(!isSubmitted && showDialog && !inquiryMode && !supplementalFormMode && isOnReview);
        window.history.replaceState({ showDialog: null }, document.title);
    }, [isSubmitted, showDialog, inquiryMode, supplementalFormMode, isOnReview]);
    useEffect(() => {
        if (importAdminMode || notifyTemplateUpdate) {
            dispatch(setPagesInitialStatusAction());
        }
    }, [importAdminMode, dispatch]);

    useEffect(() => {
        const fn = async () => {
            try {
                const isOnPayment = matchPath(history.location.pathname, {
                    path: [
                        routes.applicationFormPayments,
                        routes.externalApplicationPayments,
                    ]
                });
                if (isOnPayment) {
                    dispatch(startLoading());
                    const result = await getApplicationFee(values);
                    setApplicationFee(result);
                    dispatch(stopLoading());
                }
            } catch (e) {
                console.error(e);
                dispatch(stopLoading());
            }
        };
        fn();
    }, [values, history?.location?.pathname, setApplicationFee, dispatch]);

    const { classes, cx } = useStyles();
    const [hideSideBar, setHideSideBar] = useState(false);
    const toggleSidebarLabel = useMemo(() => intl.formatMessage({ id: hideSideBar ? 'expandPanel' : 'collapsePanel' }), [hideSideBar, intl]);
    const sidebarIcon = useMemo(() => {
        if ((hideSideBar && (!localeDirection || localeDirection === 'ltr')) ||
            (!hideSideBar && localeDirection === 'rtl')) {
            return 'arrow-double-right';
        }
        return 'arrow-double-left';
    }, [hideSideBar, localeDirection]);

    let page = null;
    if (routeMatch) {
        const pageIndex = findIndex(template.pages, { id: routeMatch.params.pageId });
        page = supplementalFormMode ? 0 : pageIndex;
    }
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const checkErrors = useCallback(errors => {
        dispatch(checkForceShowErrors({
            errors,
        }));
        // preserve force state errors, as it is completed in previous versions
        // now it should be stopped by validation
        if (notifyTemplateUpdate) {
            dispatch(setPagesInitialStatusAction());
        }
    }, [dispatch]);
    const goBackHome = useCallback(() => {
        history.push(routes.home);
    });
    const goToApplicationsHome = useCallback(() => {
        clearApplicationSession();
        rootCtx.clearTheme();
        history.push(routes.home);
    }, [clearApplicationSession, rootCtx, history]);
    const isEmbedded = useMemo(() => isInIframe(), []);
    const showMobilePanel = useMemo(() => isMobile && !inquiryMode && !supplementalFormMode, [isMobile, inquiryMode, supplementalFormMode]);
    const hideMobileContent = useMemo(() => showMobilePanel && mobilePanelOpen, [showMobilePanel, mobilePanelOpen]);
    const rootClasses = useMemo(() => {
        return cx(classes.root, {
            inquiryRoot: inquiryMode,
            mobile: isMobile,
            embedded: isEmbedded,
        });
    }, [classes, inquiryMode, isEmbedded, isMobile, template?.type]);
    const onPageClick = useCallback((pageId) => {
        dispatch(setPageAndCloseDialogAction({
            history,
            pageId,
            isAps
        }));
        if (isMobile && !inquiryMode && !supplementalFormMode) {
            setMobilePanelOpen(false);
        }
    }, [dispatch, history, isAps, formErrors, isMobile, inquiryMode, supplementalFormMode]);

    const currentMobilePanelTitle = useMemo(() => {
        if (isOnReview) {
            return intl.formatMessage({ id: 'reviewAndSubmit' });
        }
        const selectedPage = template?.pages?.find(p => p.id === routeMatch?.params?.pageId);
        return selectedPage?.titleId ? intl.formatMessage({ id: selectedPage.titleId }) : intl.formatMessage({ id: 'applicationsHome' });
    }, [isOnReview, template?.pages, routeMatch?.params?.pageId, intl]);

    const goBack = useCallback(previousPageId => {
        dispatch(goBackAction({ history, previousPageId, isAps }));
    }, [dispatch, history, isAps]);
    const goForward = useCallback(nextPageId => {
        dispatch(goForwardAction({ history, nextPageId, isAps }));
    }, [dispatch, history, isAps]);

    const handleLeaveSupplementalForm = useCallback(() => {
        const route = routes.applicationFormSupplementalItemsPage.replace(':id', application.parentApplicationId);
        history.push(route);
    }, [history, application?.parentApplicationId]);    

    const closeEmailDialog = useCallback(() => {
        setEmailDialogOpen(false);
    }, []);

    const openEmailDialog = useCallback(() => {
        const fields = getEmailPersonalizationQuestions(template, CONSTANTS.applicationDefaultPersonalizationFields);
        const clonedValues = cloneDeep(values);
        forEach(fields, key => {
            let questionValue = getApplicationValue(clonedValues, key.questionId, key.dataId);
            if (questionValue) {
                key.value = questionValue[0];
            }
        });

        setEmailQuestions(fields);
        setEmailDialogOpen(true);
    }, [values, template]);
   
    const closeSaveAdminApplicationDialog = useCallback(() => {
        setSaveAdminApplicationOpen(false);
    }, []);

    const openSaveAdminApplicationDialog = useCallback(() => {
        if (!previewMode && importAdminMode) {
            const changedValueArray = findChangedValues(originalValues, values, template, intl.locale);
            setChanges(changedValueArray);
        }
        setSaveAdminApplicationOpen(true);
    }, [currentPageId, formErrors, application.pageStatuses, previewMode, importAdminMode, values, originalValues]);

    const handleSaveAdminApplication = useCallback(async (buttonId) => {
        setSaveAdminApplicationOpen(false);
        if (buttonId == 'save') {
            try {

                const newValues = values;
                let result = await save(newValues);

                if (result && result.status === CONSTANTS.applicationProcessingStatus.error) {
                    snackbar.error(<FormattedMessage id="adminApplicationSaveWithErrorsMessage" />);
                    batch(() => {
                        dispatch(setSettingsValue({
                            name: 'revision',
                            value: result.revision,
                        }));
                        dispatch(setApplication(result));
                        dispatch(setOriginalApplicationValues(cloneDeep(result.values)));

                    });
                }
                else {
                    snackbar.success(<FormattedMessage id="applicationProcessingSaveSuccess" />);
                    let route = generatePath(routes.importsApplicationViewPage, { id: application.id });
                    history.push(route);
                }
            }
            catch (e) {
                console.error(e);
                snackbar.error(<FormattedMessage id="adminApplicationUnableToSave" />);
            }
        }
        if (buttonId == 'cancel') {
            closeSaveAdminApplicationDialog();
        }

    }, [values, originalValues, setApplicationPageCompletedStatus, application, setSettingsValue, setApplication, setOriginalApplicationValues]);

    const handleSaveAdminApplicationFormSaveWarning = useCallback(async () => {
        try {

            const newValues = values;
            let result = await save(newValues);
            if (result && result.status === CONSTANTS.applicationProcessingStatus.error) {
                snackbar.error(<FormattedMessage id="adminApplicationSaveWithErrorsMessage" />);
            }
            else {
                snackbar.success(<FormattedMessage id="applicationProcessingSaveSuccess" />);
            }
        }
        catch (e) {
            console.error(e);
            snackbar.error(<FormattedMessage id="adminApplicationUnableToSave" />);
        }
    }, [values, originalValues, setApplicationPageCompletedStatus]);

    const containerClasses = useMemo(() => {
        return cx(classes.container, {
            embedded: isEmbedded,
        });
    }, [classes, isEmbedded]);

    const closePaymentDialog = useCallback(() => setShowPaymentProcessing(false), [setShowPaymentProcessing]);

    const routeToApplicationManagement = useCallback(() => {
        let route = generatePath(routes.stagingRecords);
        history.push(route);
    }, [history]);

    return (
        <>
        <GlobalStyles styles={{ '.grecaptcha-badge': { opacity: 0 } }} />
        <div className={rootClasses}>
            <ApsSaveWarningDialog handleSaveForm={handleSaveAdminApplicationFormSaveWarning} previewMode={previewMode || false} />
            {isMobile &&
                <>
                    {previewMode &&
                        <div className={classes.returnButtonContainer}>
                            <Button onClick={onLeavePreview}>
                                <Icon className={classes.iconRoot} name="chevron-left" />
                                <FormattedMessage id="leavePreview" />
                            </Button>
                        </div>
                    }
                    {supplementalFormMode &&
                        <div className={classes.returnButtonContainer}>
                            <Button onClick={handleLeaveSupplementalForm}>
                                <Icon className={classes.iconRoot} name="chevron-left" />
                                <FormattedMessage id="backToSupplementalItems" />
                            </Button>
                        </div>
                    }
                </>
            }
            <div className={classes.titleContainer}>
                <WatchErrors onChange={checkErrors} />
                {canSendEmail && emailQuestions &&
                <SendEmailDialog
                    isEmailDialogOpen={isEmailDialogOpen}
                    onClose={() => closeEmailDialog()}
                    personalization={emailQuestions}
                    fullScreen={isMobile}
                />}
                {isMobile &&
                    <>
                        {showMobilePanel &&
                            <div className={classes.mobilePanel}>
                                {!externalMode && !importAdminMode &&
                                    <div
                                        className={classes.mobileHomeRow}
                                        onClick={goToApplicationsHome}
                                        role="link"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && goToApplicationsHome()}
                                    >
                                        <Icon name="home" />
                                        <Typography className={classes.mobileHomeText}>
                                            <FormattedMessage id="applicationsHome" />
                                        </Typography>
                                    </div>
                                }
                                <div
                                    className={classes.mobilePanelCollapsed}
                                    onClick={() => setMobilePanelOpen(!mobilePanelOpen)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === 'Enter' && setMobilePanelOpen(!mobilePanelOpen)}
                                >
                                    <span className={classes.mobilePanelTitle}>
                                        <Icon name="edit" />
                                        <Typography className={classes.mobilePanelPageText}>
                                            {currentMobilePanelTitle}
                                        </Typography>
                                    </span>
                                    <Icon name="chevron-down" className={cx(classes.mobileChevron, { [classes.mobileChevronOpen]: mobilePanelOpen })} />
                                </div>
                                <Collapse in={mobilePanelOpen} timeout={300}>
                                    <ApplicationFormSideBar
                                        page={template.pages[page]}
                                        onPageClick={onPageClick}
                                        compact
                                    />
                                </Collapse>
                            </div>
                        }
                        {!showMobilePanel &&
                        <div className={classes.formTitleContainer}>
                            {importAdminMode &&
                                <Tooltip title={intl.formatMessage({ id: 'adminStagersReturn' })}>
                                    <IconButton onClick={routeToApplicationManagement} color="gray">
                                        <BackArrow />
                                    </IconButton>
                                </Tooltip>
                            }
                            {!inquiryMode &&
                                <Typography variant="h1" className={classes.formTitle}>{intl.formatMessage({ id:template.nameId })}</Typography>
                            }
                            {!isSubmitted && !isArchived && !previewMode && !externalMode && !inquiryMode && !isOnPayment && !importAdminMode &&
                                <AutosaveStatus />
                            }
                        </div>
                        }
                        {importAdminMode &&
                            <>
                                {!!application.transferJobs?.length && canViewTransferJobs && hasPermission && !externalMode && 
                                    <ExecutionHistoryButton id={params.id} locationState={'application'} hasPermission={hasPermission}/>
                                }
                                <div>
                                    {canViewStagers &&
                                        <IconButton onClick={onViewStager} color="gray">
                                            <Icon name="applications-alt" />
                                        </IconButton>
                                    }
                                    {canSendEmail &&
                                        <Tooltip title={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                            <IconButton onClick={() => openEmailDialog()} className={classes.icon} color="gray" value={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                <Icon name="email" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </div>
                                <SimpleDialog
                                    buttonConfig={[
                                        {
                                            id: 'save',
                                            title: <FormattedMessage id="confirm" />,
                                        },
                                        {
                                            id: 'cancel',
                                            title: <FormattedMessage id="actionCancel" />
                                        }
                                    ]}
                                    title={intl.formatMessage({ id: 'adminApplicationSaveTitle' })}
                                    open={isSaveAdminApplicationOpen}
                                    closeOnClickOut={false}
                                    handleButtonClick={handleSaveAdminApplication}
                                    showTitleBarClose={true}
                                    onClose={closeSaveAdminApplicationDialog}
                                    fullScreen={isMobile}
                                >
                                    <Typography><FormattedMessage id="adminApplicationSaveConfirmation" /></Typography>
                                    {!!changes?.length &&
                                        <ApsSaveWarningTable changes={changes} />
                                    }
                                </SimpleDialog>
                                <div className={classes.saveBottonHeader}>
                                    <Button onClick={() => openSaveAdminApplicationDialog()}>
                                        <FormattedMessage id="save" />
                                    </Button>
                                </div></>
                        }
                    </>
                }
                {!isMobile &&
                    <>
                        {supplementalFormMode &&
                            <div className={classes.returnButtonContainer}>
                                <Button disabled={previewMode} onClick={handleLeaveSupplementalForm}>
                                    <Icon className={classes.iconRoot} name="chevron-left" />
                                    <FormattedMessage id="backToSupplementalItems" />
                                </Button>
                            </div>
                        }
                        <div className={classes.topBar}>
                            <div className={classes.formTitleContainer}>
                                {importAdminMode &&
                                    <Tooltip title={intl.formatMessage({ id: 'adminStagersReturn' })}>
                                        <IconButton onClick={routeToApplicationManagement} color="gray">
                                            <BackArrow />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {previewMode &&
                                    <div className={classes.returnButtonContainer}>
                                        <Button onClick={onLeavePreview}>
                                            <Icon className={classes.iconRoot} name="chevron-left" />
                                            <FormattedMessage id="leavePreview" />
                                        </Button>
                                    </div>
                                }
                                {!inquiryMode &&
                                    <Typography variant="h1" className={classes.formTitle}>
                                        {intl.formatMessage({ id:template.nameId })}
                                    </Typography> 
                                }
                                {!isSubmitted && !isArchived && !previewMode && !externalMode && !inquiryMode && !isOnPayment && !importAdminMode &&
                                    <AutosaveStatus />
                                }
                            </div>
                            {importAdminMode &&
                                <>

                                    <SimpleDialog
                                        buttonConfig={[
                                            {
                                                id: 'save',
                                                title: <FormattedMessage id="confirm" />,
                                            },
                                            {
                                                id: 'cancel',
                                                title: <FormattedMessage id="actionCancel" />
                                            }
                                        ]}
                                        title={intl.formatMessage({ id: 'adminApplicationSaveTitle' })}
                                        open={isSaveAdminApplicationOpen}
                                        closeOnClickOut={false}
                                        handleButtonClick={handleSaveAdminApplication}
                                        showTitleBarClose={true}
                                        onClose={closeSaveAdminApplicationDialog}
                                        fullScreen={isMobile}
                                        maxWidth="lg"
                                        fullWidth
                                    >
                                        <Typography><FormattedMessage id="adminApplicationSaveConfirmation" /></Typography>
                                        {!!changes?.length &&
                                            <ApsSaveWarningTable changes={changes} />
                                        }
                                    </SimpleDialog>                                   
                                    <div className={classes.saveBottonHeader}>
                                        {!!application.transferJobs?.length && canViewTransferJobs && hasPermission && !externalMode && 
                                            <ExecutionHistoryButton id={params.id} locationState={'application'} hasPermission={hasPermission}/>
                                        }
                                        {canViewStagers &&
                                            <Tooltip title={intl.formatMessage({ id: 'adminApplicationViewStager' })}>
                                                <IconButton onClick={onViewStager} color="gray" value={intl.formatMessage({ id: 'adminApplicationViewStager' })}>
                                                    <Icon name="applications-alt" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {canSendEmail &&
                                            <Tooltip title={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                <IconButton onClick={() => openEmailDialog()} className={classes.icon} color="gray" value={intl.formatMessage({ id: 'adminApplicationEmailIcon' })}>
                                                    <Icon name="email" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        <Button onClick={() => openSaveAdminApplicationDialog()}>
                                            <FormattedMessage id="save" />
                                        </Button>
                                    </div></>
                            }
                        </div>
                    </>
                }
            </div>
            {!hideMobileContent && application?.archivedOn &&
                <div>
                    <Alert
                        alertType='info'
                        open={true}
                        userDismissable={false}
                        text={<FormattedMessage id="applicationFormArchivedAlert" values={{ date: new Date(application.archivedOn).toLocaleString() }} />}
                        variant={INLINE_VARIANT}
                    />
                </div>
            }
            { !hideMobileContent && applicationStatus === CONSTANTS.applicationFormStatuses.STARTED && notifyTemplateUpdate &&
                    <Alert
                        alertType="info"
                        open={true}
                        userDismissable={false}
                        variant={INLINE_VARIANT}
                        text={<FormattedMessage id="updatedApplicationFormTextId" />}
                    />
            }
            {!hideMobileContent && applicationStatus === CONSTANTS.applicationFormStatuses.READONLY &&
                <>
                    <Typography className={classes.readonlyText}>{intl.formatMessage({ id: 'reviewInactiveForm' })}</Typography>
                    <Button aria-label={intl.formatMessage({ id: 'applicationFormClickHereToGoBack' })} variant="text" onClick={goBackHome}>{intl.formatMessage({ id: 'applicationFormClickHereToGoBack' })}</Button>
                </>
            }
            {!hideMobileContent && applicationStatus === CONSTANTS.applicationFormStatuses.WITHDRAWN &&
                <>
                    <Typography className={classes.readonlyText}>{intl.formatMessage({ id: 'applicationFormWithdrawnWarning' })}</Typography>
                </>
            }
            {!hideMobileContent && applicationStatus !== CONSTANTS.applicationFormStatuses.READONLY &&
                <Paper className={containerClasses}>
                    { isApplicationFormTemplate(template.templateType) &&
                        <div className={classes.sidebar}>
                            <Tooltip title={toggleSidebarLabel}>
                                <IconButton
                                    color="secondary"
                                    aria-label={toggleSidebarLabel}
                                    className={classes.togglePanelButton}
                                    onClick={() => setHideSideBar(!hideSideBar)}
                                >
                                    <Icon name={sidebarIcon} />
                                </IconButton>
                            </Tooltip>
                            {!isMobile &&
                                <Show hide={hideSideBar}>
                                    <ApplicationFormSideBar
                                        page={template.pages[page]}
                                        onPageClick={onPageClick}
                                    />
                                </Show>
                            }
                        </div>
                    }
                    {showSubmitted && <SubmittedDialog isOpen={showSubmitted} onClose={() => setShowSubmitted(false)} />}
                    {showPaymentProcessing &&
                        <SimpleDialog
                            buttonConfig={[
                                {
                                    id: 'ok',
                                    title: intl.formatMessage({ id: 'dialogOk' })
                                },
                            ]}
                            title={intl.formatMessage({ id: 'payProcessingHeader' })}
                            open={showPaymentProcessing}
                            closeOnClickOut={false}
                            handleButtonClick={closePaymentDialog}
                            showTitleBarClose={true}
                            onClose={closePaymentDialog}
                            fullScreen={isMobile}
                        >
                            <Typography><FormattedMessage id="payProcessingContent" /></Typography>
                        </SimpleDialog>
                    }
                    <div className={classes.content}>
                        <Switch>
                            {!canEditApp && <Route exact path={[routes.importsApplicationEditPage, routes.importsApplicationEditPageById]}>
                                <Redirect to={generatePath(routes.importsApplicationViewPage, { id: application.id })} />
                            </Route>
                            }
                            <Redirect
                                exact
                                from={routes.applicationFormStart}
                                to={isSubmitted
                                    ? routes.applicationFormSubmitPage
                                    : isPaymentPending
                                        ? applicantRoute(routes.applicationFormReviewPage, application.id) 
                                        : applicantRoute(routes.applicationFormPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.applicationFormStart}
                                to={isWithdrawn
                                    ? routes.applicationFormWithdrawnPage
                                    : applicantRoute(routes.applicationFormPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.adminTemplatePreview}
                                to={applicantRoute(routes.adminTemplatePreviewPageById, template.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.importsTemplatePreview}
                                to={applicantRoute(routes.importsTemplatePreviewPageById, template.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.importsApplicationEditPage}
                                to={applicantRoute(routes.importsApplicationEditPageById, application.id, template.pages[0].id)}
                            />
                            <Redirect
                                exact
                                from={routes.externalApplication}
                                to={applicantRoute(routes.externalApplicationPageById, application.id, template.pages[0].id)}
                            />
                            <Route exact path={[
                                routes.adminTemplatePreviewSubmitPage,
                                routes.importsTemplatePreviewSubmitPage,
                                routes.applicationFormSubmitPage,
                                routes.externalApplicationSubmitPage,
                                routes.applicationFormWithdrawnPage
                            ]}>
                                {!inquiryMode &&
                                    <Review onPageClick={onPageClick} />
                                }
                                {inquiryMode &&
                                    <InquiryConfirm commType={CONSTANTS.communicationTypes.email} />
                                }
                            </Route>
                            {template.settings.enableSupplementalItems && template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0 &&
                                <Route exact path={[
                                    routes.adminTemplatePreviewSupplementalItemsPage,
                                    routes.applicationFormSupplementalItemsPage,
                                    routes.externalApplicationSupplementalItemsPage,
                                ]}>
                                    <SupplementalItemsChecklist />
                                </Route>
                            }
                            <Route exact path={[
                                routes.adminTemplatePreviewSupportingDocumentsPage,
                                routes.applicationFormSupportingDocumentsPage,
                                routes.externalApplicationSupportingDocumentsPage,
                                routes.importsTemplatePreviewSupportingDocumentsPage,
                            ]}>
                                <SubmittedDocuments />
                            </Route>
                            <Route path={[
                                routes.applicationFormPageById,
                                routes.adminTemplatePreviewPageById,
                                routes.externalApplicationPageById,
                                routes.applicationFormSupplementalForm,
                                routes.importsTemplatePreviewPageById,
                                routes.importsApplicationEditPageById
                            ]}>
                                <>
                                    {inquiryMode && !isEmbedded &&
                                        <InquiryNavigation
                                            onBack={goBack}
                                            onContinue={goForward}
                                        />
                                    }
                                    {inquiryMode && !previewMode &&
                                        <div className='g-recaptcha' />
                                    }
                                    {template.pages[page] && (!isSubmitted && !isPaymentPending) && (!hiddenPageIds || !hiddenPageIds.includes(routeMatch.params.pageId)) &&
                                        <ApplicationFormPage page={template.pages[page]} />
                                    }
                                    {supplementalFormMode && (isSubmitted || isPaymentPending) &&
                                        <Review onPageClick={onPageClick} />
                                    }
                                </>
                            </Route>
                            <Route path={[
                                routes.applicationFormReviewPage,
                                routes.adminTemplatePreviewReviewPage,
                                routes.externalApplicationwReviewPage,
                                routes.importsTemplatePreviewReviewPage,
                            ]}>
                                <Review onPageClick={onPageClick} />
                            </Route>
                            <Route path={[
                                routes.applicationFormPayments,
                                routes.externalApplicationPayments,
                            ]}>
                                <ApplicationPayments applicationFee={applicationFee} />
                            </Route>
                        </Switch>
                    </div>
                </Paper>
            }
            {!hideMobileContent &&
                <ApplicationFormNavigationBar
                    onBack={goBack}
                    onContinue={goForward}
                    onPageClick={onPageClick}
                />
            }
        </div>
        </>
    );
};

export default ApplicationForm;
